import {
  PublicClientApplication,
  AuthenticationResult,
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { config } from "../config";

// Create a MSAL instance for authentication with explicit configuration
const msalConfig = {
  auth: {
    clientId: config.auth.clientId,
    authority: config.auth.authority,
    redirectUri: config.auth.redirectUri,
    postLogoutRedirectUri: config.auth.postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

// Log configuration for debugging
console.log("MSAL Config:", {
  clientId: msalConfig.auth.clientId,
  authority: msalConfig.auth.authority,
  redirectUri: msalConfig.auth.redirectUri,
});

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize the MSAL instance first
msalInstance.initialize().catch((error) => {
  console.error("MSAL initialization failed:", error);
});

// Get active account
export const getActiveAccount = (): AccountInfo | null => {
  const activeAccount = msalInstance.getActiveAccount();
  if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    return msalInstance.getAllAccounts()[0];
  }
  return activeAccount;
};

// Get authentication token
export const getAuthToken = async (): Promise<string | null> => {
  const account = getActiveAccount();

  if (!account) {
    return null;
  }

  const tokenRequest = {
    scopes: config.auth.scopes,
    account: account,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    return response.accessToken;
  } catch (error) {
    // If the silent token acquisition fails, fallback to interactive method
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup(tokenRequest);
        return response.accessToken;
      } catch (interactiveError) {
        console.error("Interactive authentication error:", interactiveError);
        return null;
      }
    } else {
      console.error("Token acquisition error:", error);
      return null;
    }
  }
};

// Sign in
export const signIn = async (): Promise<void> => {
  try {
    // Use a try-catch with the BrowserAuthError to check for interaction in progress
    const loginRequest = {
      scopes: config.auth.scopes,
      redirectUri: config.auth.redirectUri, // Explicitly include redirectUri
      prompt: "select_account",
    };

    console.log("Starting login redirect with request:", loginRequest);

    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      // If there's already an interaction in progress, the error will be caught here
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "interaction_in_progress"
      ) {
        console.log(
          "Authentication interaction already in progress, waiting for completion...",
        );
        return;
      }
      throw error; // Re-throw if it's not an interaction_in_progress error
    }
  } catch (error) {
    console.error("Login error:", error);
  }
};

// Sign out
export const signOut = async (): Promise<void> => {
  try {
    const account = getActiveAccount();
    if (!account) {
      console.log("No active account to sign out");
      return;
    }

    const logoutRequest = {
      account,
    };

    try {
      await msalInstance.logoutRedirect(logoutRequest);
    } catch (error) {
      // If there's already an interaction in progress, the error will be caught here
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "interaction_in_progress"
      ) {
        console.log(
          "Authentication interaction already in progress, waiting for completion...",
        );
        return;
      }
      throw error; // Re-throw if it's not an interaction_in_progress error
    }
  } catch (error) {
    console.error("Logout error:", error);
    // Fallback: force redirect to home
    window.location.href = "/";
  }
};

// Clean up auth params from URL
export const cleanAuthParams = (): void => {
  if (window.history && window.history.replaceState) {
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }
};

// Get user information
export const getUserInfo = (): AccountInfo | null => {
  return getActiveAccount();
};

// Check if the user is authenticated
export const isAuthenticated = (): boolean => {
  return getActiveAccount() !== null;
};
